import React, { useEffect, useMemo, useState, useContext } from "react";
import { BiPlus } from "react-icons/bi";
import { BsFillFileEarmarkPdfFill } from "react-icons/bs";
import { HiOutlineEye } from "react-icons/hi";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as FileSaver from "file-saver";
import swal from "sweetalert";

import Breadcrumb from "components/common/Breadcrumb";
import CheckBox from "components/common/checkBox/checkbox";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import Header from "components/common/Header/Header";
import SelectBox from "components/common/DropDownWithIocns/DropDown";
import SelectOption from "components/common/Select/Select";
import SideBar from "components/common/SideBar/SideBarNew";
import TableHeader from "components/common/Table/TableHeader";
import {
  COS_BREADCRUMB,
  COS_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import { LoginContext } from "helpers/LoginContext";
import { formatDateIntoDMYToDisplay, textTruncate } from "Utils/utils";
import { invoiceListStatusBadge } from "Utils/invoiceListStatusBadge";
import api from "store/services/cos.services";
import useCareLabels from "hooks/useCareLabels";
import { USER_ROLE } from "Constants/constant";
import { roleAccess } from "helpers/RolesPermission";

const NdisClaim = (props) => {
  const {
    claimedCaseNotesList,
    claimedCaseNotesListLoading,
    getClaimCaseNotesRequest,
    getNdisClaimMode,
    updateNdisClaimMode,
    ndisClaimMode,
    changeClaimStatus,
    changeClaimStatusLoading,
  } = props;

  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const { participantLabel } = useCareLabels();
  const isWorkerBlackPortal = roleAccess([USER_ROLE.Sp_Standard_User]);
  const navigate = useNavigate();

  const [selectedMode, setSelectedMode] = useState(ndisClaimMode || "old");
  const [checkboxes, setCheckboxes] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [updatedAction, setUpdatedAction] = useState(null);
  const [fileDownloaderLoader, setFileDownloaderLoader] = useState(false);
  const [claimPdfLoadingUuid, setClaimPdfLoadingUuid] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  useEffect(() => {
    getClaimCaseNotesRequest();
    getNdisClaimMode();
  }, []);

  useEffect(() => {
    if (ndisClaimMode) setSelectedMode(ndisClaimMode);
  }, [ndisClaimMode]);

  const isNewMode = selectedMode === "new";

  const claimedListToMap = useMemo(() => {
    if (claimedCaseNotesList && claimedCaseNotesList.length > 0) {
      if (selectedParticipant) {
        return claimedCaseNotesList.filter(
          (c) => c.participant_name === selectedParticipant.value
        );
      }
      return claimedCaseNotesList;
    }
    return [];
  }, [claimedCaseNotesList, selectedParticipant]);

  const participantOptions = useMemo(() => {
    if (claimedCaseNotesList && claimedCaseNotesList.length > 0) {
      const unique = [
        ...new Set(
          claimedCaseNotesList.map((c) => c.participant_name).filter(Boolean)
        ),
      ];
      return unique.map((name) => ({ label: name, value: name }));
    }
    return [];
  }, [claimedCaseNotesList]);

  const handleCheckboxChange = (e, uuid) => {
    const filtered = checkboxes.filter((c) => c !== uuid);
    if (e) filtered.push(uuid);
    setCheckboxes(filtered);
  };

  const toggleCheckboxes = (val) => {
    setCheckboxes(val ? claimedListToMap.map((item) => item.uuid) : []);
  };

  useEffect(() => {
    if (
      claimedListToMap &&
      checkboxes &&
      checkboxes.length === claimedListToMap.length &&
      claimedListToMap.length !== 0
    ) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [checkboxes, claimedListToMap]);

  const getSelectedCaseNoteIds = () => {
    const selectedClaims = claimedListToMap.filter((c) =>
      checkboxes.includes(c.uuid)
    );
    const allIds = [];
    selectedClaims.forEach((claim) => {
      if (claim.payload && Array.isArray(claim.payload)) {
        allIds.push(...claim.payload);
      } else if (claim.cos_case_notes && Array.isArray(claim.cos_case_notes)) {
        allIds.push(...claim.cos_case_notes.map((cn) => cn.id));
      }
    });
    return allIds;
  };

  const getCaseNoteIdsForClaimRow = (claimRow) => {
    if (!claimRow) return [];
    if (claimRow.payload && Array.isArray(claimRow.payload)) {
      return claimRow.payload.map(String);
    }
    if (
      claimRow.cos_case_notes &&
      Array.isArray(claimRow.cos_case_notes)
    ) {
      return claimRow.cos_case_notes.map((cn) => String(cn.id));
    }
    return [];
  };

  const handleDownloadClaimPdf = async (claimRow) => {
    if (isUserGracePeriodOver) return;
    const ids = getCaseNoteIdsForClaimRow(claimRow);
    if (ids.length === 0) {
      toast.warn("No case notes found for this claim.");
      return;
    }
    try {
      setClaimPdfLoadingUuid(claimRow.uuid);
      setFileDownloaderLoader(true);
      const res = await api.changeNDISClaimActionsApi({
        report: "pdf",
        cos_case_notes_ids: ids,
        download_by_claim: true,
      });
      if (res && res.report && res.report.download_url) {
        await FileSaver.saveAs(
          res.report.download_url,
          `NDIS-Claim-${claimRow.ref_no || claimRow.uuid}-PDF-File.pdf`
        );
      }
    } catch (err) {
      toast.error("Something went wrong while downloading!");
    } finally {
      setClaimPdfLoadingUuid(null);
      setFileDownloaderLoader(false);
    }
  };

  const getText = () => {
    if (updatedAction === "revert") {
      return "Are you sure you want to Revert all case notes in the selected claim(s)?";
    } else if (updatedAction === "provider_paid") {
      return "Are you sure you want to mark the selected claim(s) as Paid?";
    } else if (updatedAction === "xero") {
      return "Are you sure you want to Export the selected claim(s) to Xero?";
    }
    return "Are you sure you want to proceed?";
  };

  const handleChangeAction = async () => {
    if (isUserGracePeriodOver) return;
    if (!isNewMode) {
      setUpdatedAction("");
      return;
    }
    const caseNoteIds = getSelectedCaseNoteIds();
    if (caseNoteIds.length === 0) {
      toast.warn("No case notes found in the selected claims.");
      setUpdatedAction("");
      return;
    }

    if (updatedAction === "pdf" || updatedAction === "xlsx") {
      try {
        setFileDownloaderLoader(true);
        const res = await api.changeNDISClaimActionsApi({
          report: updatedAction,
          cos_case_notes_ids: caseNoteIds,
        });
        if (res && res.report) {
          await FileSaver.saveAs(
            res.report.download_url,
            `NDIS-Claim-${updatedAction === "xlsx" ? "EXCEL" : "PDF"}-File${
              updatedAction === "xlsx" ? ".csv" : ".pdf"
            }`
          );
        }
        setCheckboxes([]);
        setUpdatedAction("");
        setFileDownloaderLoader(false);
      } catch (err) {
        setUpdatedAction("");
        setFileDownloaderLoader(false);
        toast.error("Something went wrong while downloading!");
      }
      return;
    }

    swal({
      title: "Confirm your action?",
      text: getText(),
      icon: "",
      buttons: ["No, take me back!", "Yes, go ahead!"],
      dangerMode: updatedAction === "revert",
    }).then((confirmed) => {
      if (isUserGracePeriodOver) return;
      if (confirmed) {
        if (caseNoteIds.length > 0) {
          changeClaimStatus({
            status: updatedAction,
            cos_case_notes_ids: caseNoteIds,
            navigate,
          });
          setCheckboxes([]);
          setUpdatedAction("");
        }
      } else {
        setCheckboxes([]);
        setUpdatedAction("");
      }
    });
  };

  useEffect(() => {
    if (!isNewMode) return;
    if (updatedAction && checkboxes.length > 0) {
      handleChangeAction();
    }
  }, [updatedAction, isNewMode, checkboxes.length]);

  const handleModeChange = (e) => {
    const mode = e.target.value;
    setSelectedMode(mode);
    updateNdisClaimMode({ mode });
    setSelectedParticipant(null);
    setCheckboxes([]);
  };

  const isLoading =
    claimedCaseNotesListLoading ||
    changeClaimStatusLoading ||
    fileDownloaderLoader;

  return (
    <div className='min-h-full'>
      <SideBar />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        }  flex flex-col`}
      >
        <main className='h-screen flex-1 bg-white'>
          <Header
            title='NDIS Claims'
            breadcrumb={
              <Breadcrumb
                pages={COS_BREADCRUMB(COS_BREADCRUMB_PAGES.CREATED_NDIS_CLAIMS)}
              />
            }
            handleClick={() => navigate("/cos/ndis-claims-new")}
            btnLabel='NDIS Claim'
            icon={<BiPlus />}
            isButton
          />

          <div className='w-full px-4 py-3 bg-white'>
            <div className='mb-5 rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white px-4 py-4 shadow-sm sm:px-6'>
              <div
                className={
                  isNewMode
                    ? "grid grid-cols-1 items-start gap-x-4 gap-y-4 sm:grid-cols-1 md:grid-cols-12"
                    : "max-w-2xl"
                }
              >
                <div
                  className={`min-w-0 ${
                    isNewMode ? "md:col-span-4 lg:col-span-5" : ""
                  }`}
                >
                  <label
                    htmlFor='ndis-claim-mode-selector'
                    className='mb-1.5 block text-sm font-medium text-slate-800'
                  >
                    Claim type
                  </label>
                  <select
                    id='ndis-claim-mode-selector'
                    value={selectedMode}
                    onChange={handleModeChange}
                    className='block h-10 w-full min-w-0 rounded-lg border-slate-300 bg-white pl-3 pr-8 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-0 sm:max-w-lg lg:min-w-[18rem]'
                    disabled={isLoading}
                  >
                    <option value='new'>{participantLabel} based</option>
                    <option value='old'>Case note based</option>
                  </select>
                </div>
                {isNewMode && (
                  <div className='min-w-0 w-full md:col-span-4 lg:col-span-4'>
                    <label
                      className='mb-1.5 block text-sm font-medium text-slate-800'
                      htmlFor='participant-claim-filter'
                    >
                      Show claims for
                    </label>
                    <div
                      id='participant-claim-filter'
                      className='[&_.select-basic-single>div]:!min-h-[2.5rem]'
                    >
                      <SelectOption
                        isClearable={true}
                        isDisabled={isLoading}
                        cssClass=''
                        name='participant-filter'
                        isSearch={true}
                        data={participantOptions}
                        selectedOption={selectedParticipant}
                        handleChange={(e) => setSelectedParticipant(e)}
                        placeHolder='All participants'
                      />
                    </div>
                  </div>
                )}
                {isNewMode && (
                  <div className='w-full min-w-0 md:col-span-4 md:min-w-0 lg:col-span-3'>
                    <span className='mb-1.5 block text-sm font-medium text-slate-800'>
                      Bulk action
                    </span>
                    <div className='w-full [&>div]:!block [&>div]:w-full [&_button]:!h-10 [&_button]:!min-h-[2.5rem] [&_button]:!py-0'>
                      <SelectBox
                        disabled={
                          (checkboxes.length > 0 ? false : true) ||
                          isUserGracePeriodOver
                        }
                        setUpdatedAction={setUpdatedAction}
                        isForCosModule={false}
                        allowXeroExport={!isWorkerBlackPortal}
                        showExportReports={true}
                        hideCancelDsBulkOptions
                        hideEmailPlanManagerBulkOption
                        hideBulkPdfReport={isNewMode}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='sm:px-6 lg:px-4 py-4'>
              <div className='flex flex-col'>
                <div className='min-w-full'>
                  <div className='shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
                    <div className='table-wrp block'>
                      <table className='min-w-full divide-y divide-gray-300'>
                        <thead
                          className='bg-blue-50 sticky z-[5] capitalize'
                          style={{ top: "80px" }}
                        >
                          <tr>
                            {isNewMode && (
                              <th
                                scope='col'
                                className='pl-3 text-center text-sm font-semibold text-gray-900 bg-blue-50'
                              >
                                <CheckBox
                                  isDisabled={isUserGracePeriodOver}
                                  checked={allSelected}
                                  handleChange={(e) => {
                                    if (isUserGracePeriodOver) return;
                                    if (
                                      claimedListToMap &&
                                      claimedListToMap.length > 0
                                    ) {
                                      setAllSelected(e);
                                      toggleCheckboxes(e);
                                    }
                                  }}
                                />
                              </th>
                            )}
                            <TableHeader
                              sortName='reference'
                              isSort={false}
                              label={"Reference"}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='participant'
                              label={participantLabel}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='created_by'
                              label={"Created By"}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='created_date'
                              label={"Created Date"}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='delivered_date'
                              label={"Delivered From"}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='delivered_to'
                              label={"Delivered To"}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='case_notes'
                              label={"Case Notes"}
                              cssClass='text-center'
                            />
                            <TableHeader
                              isSort={false}
                              sortName='total_amount'
                              label={"Total Amount"}
                              cssClass='text-center'
                            />
                            <TableHeader
                              isSort={false}
                              sortName='claim_status'
                              label={"Status"}
                              cssClass='text-center'
                            />
                            <TableHeader
                              isSort={false}
                              sortName='status'
                              label={"Action"}
                              cssClass='text-center'
                            />
                            <th>
                              <div className='sr-only'>View</div>
                            </th>
                          </tr>
                        </thead>

                        <tbody className='divide-y divide-gray-200 bg-white'>
                          {isLoading ? (
                            <tr>
                              <td
                                className='w-full text-center py-4 bg-gray-50'
                                colSpan={isNewMode ? 12 : 11}
                              >
                                <CircularLoader
                                  classes='flex justify-center items-center'
                                  classloader='loader-l'
                                />
                              </td>
                            </tr>
                          ) : !claimedListToMap ||
                            claimedListToMap.length === 0 ? (
                            <tr>
                              <td
                                colSpan={isNewMode ? 12 : 11}
                                className='py-6 text-center text-red-600'
                              >
                                No Data Found !
                              </td>
                            </tr>
                          ) : (
                            claimedListToMap.map((items, index) => (
                              <tr
                                key={index}
                                className='text-left even:bg-white odd:bg-gray-50'
                              >
                                {isNewMode && (
                                  <td className='whitespace-nowrap pl-3 py-4 text-sm text-gray-500 text-center'>
                                    <CheckBox
                                      isDisabled={isUserGracePeriodOver}
                                      handleChange={(e) => {
                                        if (isUserGracePeriodOver) return;
                                        handleCheckboxChange(e, items.uuid);
                                      }}
                                      checked={
                                        checkboxes?.indexOf(items.uuid) !== -1
                                      }
                                    />
                                  </td>
                                )}
                                <td className=' px-3 py-4 text-sm text-gray-500'>
                                  {items?.ref_no}
                                </td>
                                <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                                  {items?.participant_name || "—"}
                                </td>
                                <td
                                  title={items?.created_by}
                                  className='whitespace-nowrap px-3 py-4 text-sm text-gray-500 capitalize'
                                >
                                  {textTruncate(items?.created_by, 30, 29)}
                                </td>
                                <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                                  {formatDateIntoDMYToDisplay(
                                    items.created_date
                                  )}
                                </td>
                                <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                                  {formatDateIntoDMYToDisplay(
                                    items.delivered_from
                                  )}
                                </td>
                                <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                                  {formatDateIntoDMYToDisplay(
                                    items.delivered_to
                                  )}
                                </td>
                                <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center'>
                                  {items?.case_notes}
                                </td>
                                <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center'>
                                  {`$${
                                    parseFloat(items?.total_amount).toFixed(
                                      2
                                    ) || 0
                                  }`}
                                </td>
                                <td className='whitespace-nowrap px-3 py-4 text-sm text-center'>
                                  {invoiceListStatusBadge(items?.status)}
                                </td>
                                <td className='px-3 py-4 text-sm text-center text-gray-500'>
                                  <div className='flex justify-center gap-x-2 items-center'>
                                    <Link
                                      className='icon-eye-wrapper inline-block'
                                      to={`/cos/ndis-claims-review/${items.uuid}`}
                                      title='View'
                                    >
                                      <HiOutlineEye className='icon-size' />
                                    </Link>
                                    {isNewMode && (
                                      <span
                                        title='Download PDF'
                                        role='button'
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                          if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                          ) {
                                            e.preventDefault();
                                            if (!isUserGracePeriodOver)
                                              handleDownloadClaimPdf(items);
                                          }
                                        }}
                                        onClick={() => {
                                          if (!isUserGracePeriodOver)
                                            handleDownloadClaimPdf(items);
                                        }}
                                        className={`text-rose-600 border border-rose-600 p-1 rounded-md hover:text-white hover:border-rose-600 hover:bg-rose-600 hover:transition-all-custom ${
                                          isUserGracePeriodOver ||
                                          claimPdfLoadingUuid === items.uuid
                                            ? "pointer-events-none opacity-50"
                                            : "cursor-pointer"
                                        }`}
                                      >
                                        <BsFillFileEarmarkPdfFill className='h-4 w-4' />
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default NdisClaim;
